/* ==========================================================================
   Meet The Montigo You — Result page controller (bl_personality-result.liquid)

   THREE DISPLAY MODES (deck, page 1):
     A) Quiz result   — no URL params, sessionStorage has a completed quiz.
     B) Browsing other — URL has ?type= for a personality that is NOT the
                        visitor's own, but the visitor HAS taken the quiz
                        (e.g. clicked a card in the all-16 grid). Share is
                        hidden — you only share your OWN result (client
                        13/07/2026; CSS [data-mode='B'] rule).
     C) Direct visit  — URL has ?type= and the visitor has NOT taken the quiz.

   No ?type= AND no quiz data → NO redirect (client 08/07/2026): the first
   personality block renders as an anonymous Mode C view, both on the
   storefront and in the theme editor.

   HERO CARD: pre-designed full card image per gender. Overlays added here:
     • greeting "Hello <name>, / Meet the Montigo You!"  (Mode A only)
     • 3 recommended product images in the card's placeholder row
   The same overlays are drawn onto the canvas for share/download, so the
   exported PNG carries the name + products embedded.

   PRODUCT SETS: window.MTY_PRODUCT_SETS (rendered by the section from the
   personality_test_product_list metaobjects), keyed by lowercased type code:
     { name, price, available, image|null, parts: [{kind, available, image,
       id}], }  — image = complete_image, parts fall back to the variants'
     custom.personalization_variant_image (→ variant/product image).
   Only sets with available=true (all 3 variants in stock) are eligible for
   random recommendation. Empty/missing data → visual placeholders.

   URL contract (share links):  ?type=enfp&gender=female
   ========================================================================== */

(function () {
  'use strict';

  /* Same storage shape as assets/bl_personality-test.js (quiz page). The two
     files run on different pages, so the helper is duplicated on purpose. */
  var STORAGE_KEY = 'mty_quiz_v1';

  function readSession() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  /* ------------------------------------------------------------------ */
  /* Stacked set composites                                               */
  /*                                                                      */
  /* When a set has no complete_image, its cap/bottle/boot variant         */
  /* renders are STACKED into one composite — same geometry as the old     */
  /* 2D customiser. Presets extracted from the previous theme's            */
  /* customise pages (page.customise-*.json, step blocks "Choose Base      */
  /* Colour / Cap / Boot"), keyed by the BOTTLE variant's product TYPE     */
  /* (a cap sits differently on every bottle, so cap/boot geometry         */
  /* belongs to the bottle's preset). Layer = [x, y, w]:                   */
  /*   x = horizontal centre, % of the square media box                    */
  /*   y = layer's BOTTOM edge, % up from the box bottom                   */
  /*   w = layer width as a fraction of the box (images keep their         */
  /*       native aspect — cap/boot renders ship on wide strips cut        */
  /*       from the same 800px canvas as the bottles)                      */
  /* Module scope (not inside init) so the theme-editor audit stacker      */
  /* below can reuse the exact same geometry.                              */
  var STACK_PRESETS = {
    'ACE-18OZ':    { bottle: [50, 3, 0.9],  cap: [50, 71.08, 0.9],    boot: [50, 2, 0.9] },      // Ace Bottle Medium
    'ACE-32OZ':    { bottle: [50, 3, 0.78], cap: [50, 63.6, 0.7],     boot: [50.2, 1.5, 0.6] }, // Ace Bottle Mega
    'ACE-7OZ':     { bottle: [50, 3, 0.9],  cap: [50, 71, 0.9],       boot: [50, 2, 0.9] },      // Ace Bottle Micro
    'BBMV-18OZ':   { bottle: [50, 3, 0.9] },                                                      // Bubble Bottle Move (lid built in)
    'ROAMER-33OZ': { bottle: [50, 0, 0.78],  cap: [50, 63, 0.79] },                                // Roamer Tumbler Large
    'ROAMER-24OZ': { bottle: [50, 3, 0.95],  cap: [50, 64.65, 0.95] },                                // Roamer Tumbler Regular
    'ROAMER-16OZ': { bottle: [50, 3, 0.78],  cap: [50, 39, 0.78] },                                // Roamer Tumbler Small
    'SBG2-24OZ':   { bottle: [50, 3, 0.95], cap: [49.8, 76, 0.86],    boot: [50, 1.8, 0.93] },   // Go Bottle 700ml
    'SBG2-40OZ':   { bottle: [50, 3, 0.78], cap: [49.8, 76.54, 0.82], boot: [50, 1.8, 0.75] },   // Go Bottle 1200ml
    'SENSE-12OZ':  { bottle: [50, 20, 1],   cap: [50, 62.3, 1] },                                 // Sense Coffee Cup Regular
    'SENSE-16OZ':  { bottle: [50, 5, 1],    cap: [50, 62.25, 1] },                                 // Sense Coffee Cup Large
    'BUB-14OZ':  { bottle: [50, 3, 0.85],  cap: [50, 55, 0.89] }                                  // Bubble Bottle Regular
  };
  var STACK_DEFAULT = { bottle: [50, 3, 0.9], cap: [50, 71, 0.9], boot: [50, 2, 0.9] };

  /* Layer order: bottle, then boot, then cap. On-page the CSS z-indexes
     put cap/boot ABOVE the bottle on white fills (client 10/07/2026 —
     .mty-set-stack__img--* in bl_personality.css); the share canvas
     paints the same white fills before each cap/boot layer. Unknown
     bottle type → default preset; a part the preset has no entry for
     falls back to the default's. */
  function stackLayers(set) {
    var byKind = {};
    (set.parts || []).forEach(function (p) { byKind[p.kind] = p; });
    var bottle = byKind.bottle;
    var key = bottle && bottle.type ? String(bottle.type).toUpperCase().trim() : '';
    var preset = STACK_PRESETS[key] || STACK_DEFAULT;
    var layers = [];
    ['bottle', 'boot', 'cap'].forEach(function (kind) {
      var p = byKind[kind];
      if (!p || !p.image) return;
      layers.push({ src: p.image, geo: preset[kind] || STACK_DEFAULT[kind], kind: kind });
    });
    return layers;
  }

  /* Optional per-set reframe (client 13/07/2026): integer metaobject fields
     scale_factor (0-50 → scale(1.00-1.50)) and vertical_anchor (0-100 →
     transform-origin: 50% N%), carried on the set as {scale, anchor} by the
     MTY_PRODUCT_SETS island (and data-scale/-anchor on the audit boxes).
     Returns null when there's nothing to apply (scale 0/unset), so untouched
     sets render exactly as before. The share canvas mirrors the same maths
     about the same origin — keep the two in sync. */
  function stackTransform(set) {
    var sf = parseInt(set && set.scale, 10);
    if (!sf || sf <= 0) return null;
    sf = Math.min(sf, 50);
    var anchor = parseInt(set && set.anchor, 10);
    if (isNaN(anchor)) anchor = 50;
    anchor = Math.max(0, Math.min(anchor, 100));
    return { scale: 1 + sf / 100, anchor: anchor };
  }

  /* A set's visual: complete_image if present, else the stacked
     cap + bottle + boot composite (with the optional per-set reframe —
     complete_image sets render as-is, the fields only affect stacks). */
  function setMediaHtml(set) {
    /* loading="eager" everywhere here, NEVER lazy (client bug 13/07/2026,
       blank audit boxes / storefront tiles): these imgs are JS-inserted
       with no width/height attributes, so they have ZERO height until the
       file loads — and Chrome never fetches a zero-size lazy image. The
       deadlock only breaks when the file happens to be in the browser
       cache, which is why SOME sets appeared and others stayed blank. */
    if (set.image) {
      return '<img src="' + set.image + '" alt="" loading="eager">';
    }
    var layers = stackLayers(set);
    if (layers.length) {
      var t = stackTransform(set);
      var reframe = t
        ? ' style="transform:scale(' + t.scale + ');transform-origin:50% ' + t.anchor + '%"'
        : '';
      return '<span class="mty-set-stack"' + reframe + '>' + layers.map(function (l) {
        return '<img class="mty-set-stack__img mty-set-stack__img--' + l.kind +
          '" src="' + l.src + '" alt="" loading="eager" style="left:' +
          l.geo[0] + '%;bottom:' + l.geo[1] + '%;width:' + (l.geo[2] * 100) + '%">';
      }).join('') + '</span>';
    }
    return '<span class="mty-product-ph">Product set image</span>';
  }

  /* ------------------------------------------------------------------ */
  /* Theme-editor product-set audit: restack the part renders             */
  /*                                                                      */
  /* The audit block (bl_personality-result.liquid, design mode only)      */
  /* renders composing-from-variants sets as a flat row of part imgs with  */
  /* data-kind attrs + the bottle type on the box. Restack them into the   */
  /* same composite shoppers see (client 10/07/2026). Parts with no image  */
  /* at all keep their red "no image" note via the badges below the card.  */
  function stackAdminPreviews() {
    document.querySelectorAll('[data-mty-admin-stack]').forEach(function (box) {
      if (box.hasAttribute('data-mty-stacked')) return;
      var parts = [];
      box.querySelectorAll('img[data-kind]').forEach(function (img) {
        parts.push({
          kind: img.getAttribute('data-kind'),
          image: img.getAttribute('src'),
          type: img.getAttribute('data-kind') === 'bottle' ? box.getAttribute('data-bottle-type') : null
        });
      });
      var html = setMediaHtml({
        image: null,
        parts: parts,
        // Same per-set reframe as the storefront (data attrs mirror the
        // scale_factor / vertical_anchor metaobject fields).
        scale: box.getAttribute('data-scale'),
        anchor: box.getAttribute('data-anchor')
      });
      if (html.indexOf('mty-set-stack') === -1) return; // nothing stackable
      box.innerHTML = html;
      box.setAttribute('data-mty-stacked', '');
    });
  }

  function init() {
    var root = document.querySelector('[data-mty-result]');
    if (!root || root.hasAttribute('data-mty-ready')) return;
    root.setAttribute('data-mty-ready', '');

    var quizUrl = root.getAttribute('data-quiz-url') || '/pages/meet-the-montigo-you';
    var defaultGender = root.getAttribute('data-default-gender') || 'male';

    var params = new URLSearchParams(window.location.search);
    var session = readSession();

    /* ---------------------------------------------------------------- */
    /* Resolve type / gender / mode                                       */
    /* ---------------------------------------------------------------- */

    var paramType = (params.get('type') || '').toUpperCase();
    var sessionType = (session.type || '').toUpperCase();

    function templateFor(type) {
      return root.querySelector('template[data-personality="' + type.toLowerCase() + '"]');
    }

    var type = null;
    var mode = null; // 'A' | 'B' | 'C'

    if (paramType && templateFor(paramType)) {
      type = paramType;
      mode = sessionType === paramType ? 'A' : sessionType ? 'B' : 'C';
    } else if (sessionType && templateFor(sessionType)) {
      type = sessionType;
      mode = 'A';
    } else {
      /* No shareable type and no quiz data: STAY on the result page (client
         08/07/2026 — no redirect to the quiz cover). Render the first
         personality block as an anonymous Mode C view; same path keeps the
         section previewable in the theme editor. */
      var firstTpl = root.querySelector('template[data-personality]');
      if (!firstTpl) return;
      type = firstTpl.getAttribute('data-personality').toUpperCase();
      mode = 'C';
    }

    var paramGender = (params.get('gender') || '').toLowerCase();
    var gender = ['male', 'female'].indexOf(paramGender) >= 0
      ? paramGender
      : (session.gender === 'male' || session.gender === 'female')
        ? session.gender
        : defaultGender;

    /* ---------------------------------------------------------------- */
    /* Render the hero card (pre-designed image + overlays)               */
    /* ---------------------------------------------------------------- */

    var mount = root.querySelector('[data-mty-mount]');
    mount.innerHTML = ''; // re-init safety (theme editor re-renders)
    var tpl = templateFor(type);
    var card = tpl.content.firstElementChild.cloneNode(true);
    var personaName = card.getAttribute('data-name') || type;

    // Keep only the artwork for the resolved gender.
    card.querySelectorAll('[data-gender]').forEach(function (el) {
      if (el.getAttribute('data-gender') !== gender) el.remove();
    });

    // Greeting overlay — Mode A only; the card artwork already carries the
    // personality copy for anonymous views (Modes B/C).
    var greeting = card.querySelector('[data-mty-greeting]');
    var greetingLines = null;
    if (mode === 'A' && session.name) {
      greetingLines = ['Hello ' + session.name + ',', 'Meet the Montigo You!'];
      greeting.textContent = '';
      greeting.append(greetingLines[0]);
      greeting.appendChild(document.createElement('br'));
      greeting.append(greetingLines[1]);
    } else if (greeting) {
      greeting.remove();
    }

    mount.appendChild(card);
    root.setAttribute('data-mode', mode);

    /* Share is CSS-hidden for Modes B and C ([data-mode] rules) — only your
       own result (Mode A) is shareable. B and C also swap the card headline
       and the sets box title/subtitle to their anonymous copy (data-anon-text,
       from the section's "— anonymous view" settings): the result on screen
       isn't the visitor's own, so no "your"/"you" phrasing (client
       13/07/2026 — previously Mode C only). */
    var headlineEl = card.querySelector('[data-mty-card-headline]');
    if (mode !== 'A') {
      [
        headlineEl,
        root.querySelector('.mty-result-block:not(.mty-klaviyo) .mty-result-block__title'),
        root.querySelector('.mty-result-block:not(.mty-klaviyo) .mty-result-block__subtitle')
      ].forEach(function (el) {
        var anon = el && el.getAttribute('data-anon-text');
        if (anon) el.textContent = anon;
      });
    }
    var headlineText = headlineEl ? headlineEl.textContent.trim() : '';

    /* ---------------------------------------------------------------- */
    /* Product sets                                                       */
    /* ---------------------------------------------------------------- */

    var PLACEHOLDER_SETS = [];
    for (var i = 1; i <= 15; i++) {
      PLACEHOLDER_SETS.push({
        name: 'Montigo Set ' + i,
        price: 'RM —',
        available: false,
        image: null,
        parts: [],
        placeholder: true
      });
    }

    /* Recommendation rule: pick 3 at random from the sets whose cap, bottle
       and boot variants are ALL available. If the personality has sets but
       none fully in stock, show the first 3 with add-to-cart disabled; if it
       has no sets at all, show placeholders. */
    function pickSets() {
      var byType = window.MTY_PRODUCT_SETS || {};
      /* The metaobject lists key their sets by personality NAME ("The
         Vibrant Explorer") — try that first, but accept the MBTI code too
         in case entries are ever re-keyed. */
      var sets = byType[(personaName || '').toLowerCase().trim()] ||
        byType[type.toLowerCase()] || [];
      if (!sets.length) return pickRandom(PLACEHOLDER_SETS, 3);
      var eligible = sets.filter(function (s) { return s.available; });
      return eligible.length ? pickRandom(eligible, 3) : sets.slice(0, 3);
    }

    function pickRandom(list, count) {
      var pool = list.slice();
      var picked = [];
      while (picked.length < count && pool.length) {
        picked.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
      }
      return picked;
    }

    var pickedSets = pickSets();

    /* Stacked set composites: STACK_PRESETS / stackLayers / setMediaHtml
       live at module scope (top of this file) so the theme-editor audit
       stacker can reuse them. */

    // "Your Perfect Montigo sets" cards
    var setsMount = root.querySelector('[data-mty-sets]');
    if (setsMount) {
      setsMount.innerHTML = '';
      pickedSets.forEach(function (set) {
        var el = document.createElement('div');
        el.className = 'mty-set-card';
        /* Blank cap/boot refs are legitimate (cups, tumblers, lidded
           bottles) — buyable as long as every referenced part is
           available (the Liquid availability flag) and something exists
           to add. */
        var buyableParts = (set.parts || []).filter(function (p) { return p.id; });
        var canBuy = set.available && buyableParts.length > 0;
        el.innerHTML =
          '<div class="mty-set-card__media">' + setMediaHtml(set) + '</div>' +
          '<span class="mty-set-card__name"></span>' +
          '<span class="mty-set-card__price"></span>' +
          '<button type="button" class="mty-btn mty-set-card__atc"' + (canBuy ? '' : ' disabled title="Unavailable"') + '><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.3335 7.49996V4.99996C13.3335 3.15901 11.8411 1.66663 10.0001 1.66663C8.1592 1.66663 6.66681 3.15901 6.66681 4.99996V7.49996M2.99348 8.6266L2.49348 13.9599C2.35132 15.4763 2.28023 16.2346 2.53185 16.8202C2.75289 17.3347 3.14024 17.76 3.63183 18.0281C4.19142 18.3333 4.95295 18.3333 6.47602 18.3333H13.5243C15.0473 18.3333 15.8089 18.3333 16.3685 18.0281C16.86 17.76 17.2474 17.3347 17.4684 16.8202C17.7201 16.2346 17.649 15.4763 17.5068 13.9599L17.0068 8.6266C16.8868 7.34608 16.8267 6.70582 16.5388 6.22176C16.2851 5.79545 15.9104 5.45422 15.4623 5.2415C14.9535 4.99996 14.3104 4.99996 13.0243 4.99996L6.97602 4.99996C5.68989 4.99996 5.04682 4.99996 4.53799 5.24149C4.08987 5.45422 3.71515 5.79545 3.46153 6.22176C3.17355 6.70582 3.11353 7.34608 2.99348 8.6266Z" stroke="white" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg></button>';
        // Card title = the bottle's product title (client spec); the
        // metaobject display_name is only a fallback for bottle-less sets.
        el.querySelector('.mty-set-card__name').textContent = set.bottleTitle || set.name || '';
        el.querySelector('.mty-set-card__price').textContent = set.price || '';
        if (canBuy) {
          el.querySelector('.mty-set-card__atc').addEventListener('click', function () {
            addSetToCart(set, this);
          });
        }
        setsMount.appendChild(el);
      });
    }

    // Product images in the card's pre-set placeholder row (and share canvas)
    var cardProducts = card.querySelector('[data-mty-card-products]');
    if (cardProducts) {
      cardProducts.innerHTML = '';
      pickedSets.forEach(function (set) {
        var el = document.createElement('div');
        el.className = 'mty-result-card__product';
        // Media in its own square wrapper (the positioned anchor for
        // .mty-set-stack) + the bottle's product title below it, inside
        // the white tile (client 10/07/2026). Title falls back like the
        // sets cards: bottle title, then the metaobject display name.
        el.innerHTML = '<div class="mty-result-card__product-media">' + setMediaHtml(set) + '</div>' +
          '<span class="mty-result-card__product-name"></span>';
        el.querySelector('.mty-result-card__product-name').textContent = set.bottleTitle || set.name || '';
        cardProducts.appendChild(el);
      });
    }

    /* Add every referenced variant of a set to the cart, then refresh the
       mini-cart (theme events documented in assets/custom.js). */
    function addSetToCart(set, btn) {
      var items = (set.parts || [])
        .filter(function (p) { return p.id; })
        .map(function (p) { return { id: p.id, quantity: 1 }; });
      btn.disabled = true;
      // Spinner between the click and "Added!" (client 10/07/2026) — same
      // idea as the product page's ATC. Styles: .mty-set-card__atc.is-loading.
      btn.classList.add('is-loading');
      fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('cart-add-failed');
          return r.json();
        })
        .then(function () {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          var miniCart = document.getElementById('mini-cart');
          if (miniCart) miniCart.open = true;
          btn.textContent = 'Added!';
        })
        .catch(function () {
          btn.textContent = 'Try again';
        })
        .finally(function () {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        });
    }

    /* ---------------------------------------------------------------- */
    /* Share — canvas composite of the pre-designed card                  */
    /* ---------------------------------------------------------------- */

    function shareUrl() {
      var url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('type', type.toLowerCase());
      url.searchParams.set('gender', gender);
      return url.toString();
    }

    function loadImage(src) {
      return new Promise(function (resolve) {
        if (!src) return resolve(null);
        var img = new Image();
        img.crossOrigin = 'anonymous'; // Shopify CDN sends CORS headers
        img.onload = function () { resolve(img); };
        img.onerror = function () { resolve(null); };
        img.src = src;
      });
    }

    /* Compose the exported card exactly like the on-page overlay:
       base card + greeting (Mode A) + the 3 recommended product images in
       the placeholder row. Geometry mirrors the Figma card (340×604):
         greeting at 6.46% / 6.15%, products row 88.2% wide at 70.5% top,
         3 white frames of 26.3% card width. */
    function buildShareImage() {
      var baseSrc = card.getAttribute('data-share-card-' + gender);
      if (!baseSrc) return Promise.reject(new Error('no-base-card'));

      /* Per set: either one complete image (geo null → contain-fit) or the
         stacked layers, drawn with the exact geometry the on-page
         .mty-set-stack uses. */
      var setSpecs = pickedSets.map(function (set) {
        if (set.image) return [{ src: set.image, geo: null }];
        return stackLayers(set);
      });
      var flat = [];
      setSpecs.forEach(function (spec) { spec.forEach(function (l) { flat.push(l); }); });

      return Promise.all([loadImage(baseSrc)].concat(flat.map(function (l) { return loadImage(l.src); })))
        .then(function (images) {
          var base = images[0];
          if (!base) throw new Error('base-card-load-failed');
          var flatImgs = images.slice(1);
          var cursor = 0;
          var setImgs = setSpecs.map(function (spec) {
            return spec.map(function (l) {
              return { img: flatImgs[cursor++], geo: l.geo, kind: l.kind };
            }).filter(function (l) { return l.img; });
          });

          var W = 1080;
          var H = Math.round(W * 604 / 340); // ≈ 9:16, matches the card art
          var canvas = document.createElement('canvas');
          canvas.width = W;
          canvas.height = H;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(base, 0, 0, W, H);

          // Greeting — Mode A only (same rule as the on-page overlay).
          if (greetingLines) {
            var fontPx = Math.round(12 / 340 * W); // 12px @ 340 design width
            ctx.font = '700 ' + fontPx + 'px Poppins, sans-serif';
            ctx.fillStyle = '#000000';
            ctx.textBaseline = 'top';
            var gx = 0.0646 * W;
            var gy = 0.065 * H; // matches .mty-result-card__greeting top: 6.5%
            ctx.fillText(greetingLines[0], gx, gy);
            ctx.fillText(greetingLines[1], gx, gy + fontPx * 1.35);
          }

          // Card headline — same overlay as on-page
          // (.mty-result-card__headline: 6.46% / 66.2%, bold 12 @ 340).
          if (headlineText) {
            var hlPx = Math.round(12 / 340 * W);
            ctx.font = '700 ' + hlPx + 'px Poppins, sans-serif';
            ctx.fillStyle = '#000000';
            ctx.textBaseline = 'top';
            ctx.fillText(headlineText, 0.0646 * W, 0.662 * H);
          }

          // Products row (width 84% — matches .mty-result-card__products).
          var rowW = 0.84 * W;
          var rowX = (W - rowW) / 2;
          var rowY = 0.705 * H; // matches .mty-result-card__products top: 70.5%
          var frame = 0.263 * W;
          var gap = (rowW - frame * 3) / 2;
          var radius = Math.round(15 / 340 * W);

          /* Tile interior — mirrors .mty-result-card__product + __product-name
             (client 10/07/2026: bottle title inside the white tile on the
             exported card too). Padding 2cqw top / 1.8cqw sides+bottom,
             square media zone, then a FIXED 2-line title box (font 2.4cqw,
             line-height 1.25, height 2.5em) so all three tiles come out the
             same height regardless of how the titles wrap. */
          var padTop = 0.02 * W;
          var padSide = 0.018 * W;
          var padBottom = 0.018 * W;
          var media = frame - padSide * 2;
          var titleFont = 0.024 * W;
          var titleLineH = titleFont * 1.25;
          var titleBox = titleFont * 2.5;
          var titleTop = padTop + media + (4 / 340) * W; // margin-top: 4px @340
          var tileH = titleTop + titleBox + padBottom;

          // Word-wrap into at most 2 lines; ellipsize the second on overflow
          // (mirrors -webkit-line-clamp: 2). Assumes ctx.font is already set.
          function wrapTitle(text, maxWidth) {
            var words = String(text || '').split(/\s+/).filter(Boolean);
            var lines = [];
            var cur = '';
            words.forEach(function (word) {
              var test = cur ? cur + ' ' + word : word;
              if (!cur || ctx.measureText(test).width <= maxWidth) {
                cur = test;
              } else {
                lines.push(cur);
                cur = word;
              }
            });
            if (cur) lines.push(cur);
            if (lines.length > 2) {
              var rest = lines.slice(1).join(' ');
              while (rest && ctx.measureText(rest + '…').width > maxWidth) {
                rest = rest.slice(0, -1).replace(/\s+$/, '');
              }
              lines = [lines[0], rest + '…'];
            }
            return lines;
          }

          setImgs.forEach(function (layers, idx) {
            var x = rowX + idx * (frame + gap);
            var mediaX = x + padSide;
            var mediaY = rowY + padTop;
            ctx.save();
            if (ctx.roundRect) {
              ctx.beginPath();
              ctx.roundRect(x, rowY, frame, tileH, radius);
              ctx.fillStyle = '#ffffff';
              ctx.fill();
              ctx.clip();
            } else {
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(x, rowY, frame, tileH);
            }
            if (!layers.length) {
              ctx.fillStyle = '#e7e9f5';
              ctx.fillRect(mediaX + media * 0.08, mediaY + media * 0.08, media * 0.84, media * 0.84);
            }
            /* Per-set reframe (client 13/07/2026) — same scale_factor /
               vertical_anchor transform the on-page .mty-set-stack gets,
               scaled about the same origin (50% / anchor% of the inset
               stack box) and clipped to the media zone (mirrors the DOM
               media wrappers' overflow:hidden). Own save/restore so the
               title below draws untransformed. Stacked composites only —
               complete_image tiles (no geo) are never reframed. */
            var reframe = layers.length && layers[0].geo
              ? stackTransform(pickedSets[idx])
              : null;
            ctx.save();
            if (reframe) {
              ctx.beginPath();
              ctx.rect(mediaX, mediaY, media, media);
              ctx.clip();
              var rfInset = media * 0.05;
              var rfBox = media - rfInset * 2;
              var rfX = mediaX + rfInset + rfBox / 2;
              var rfY = mediaY + rfInset + (reframe.anchor / 100) * rfBox;
              ctx.translate(rfX, rfY);
              ctx.scale(reframe.scale, reframe.scale);
              ctx.translate(-rfX, -rfY);
            }
            layers.forEach(function (l) {
              if (!l.geo) {
                // complete_image → contain-fit inside the media zone, like
                // the on-page <img> (max-width/height 100% of the wrapper)
                var s = Math.min(media / l.img.width, media / l.img.height);
                var dw = l.img.width * s;
                var dh = l.img.height * s;
                ctx.drawImage(l.img, mediaX + (media - dw) / 2, mediaY + (media - dh) / 2, dw, dh);
                return;
              }
              // Stacked layer — mirror .mty-set-stack (inset 5% of the media
              // wrapper); geo = [centreX%, bottomY%, widthFraction], image
              // keeps its native aspect.
              var inset = media * 0.05;
              var box = media - inset * 2;
              var lw = l.geo[2] * box;
              var lh = lw * (l.img.height / l.img.width);
              var lx = mediaX + inset + (l.geo[0] / 100) * box - lw / 2;
              var lyBottom = mediaY + inset + box - (l.geo[1] / 100) * box;
              // Cap/boot sit on a white fill so the bottle can't show
              // through their transparent regions — mirrors the on-page
              // .mty-set-stack__img--cap/--boot background (client
              // 10/07/2026). Draw order (bottle → boot → cap) already
              // matches the on-page z-indexes.
              if (l.kind === 'cap' || l.kind === 'boot') {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(lx, lyBottom - lh, lw, lh);
              }
              ctx.drawImage(l.img, lx, lyBottom - lh, lw, lh);
            });
            ctx.restore(); // end of the per-set reframe scope

            // Bottle title — same source as the on-page tile
            // (.mty-result-card__product-name: bottleTitle || display name).
            var title = pickedSets[idx] && (pickedSets[idx].bottleTitle || pickedSets[idx].name);
            if (title) {
              // Same family as the on-page tile (--mty-font-body) — Poppins
              // metrics run wider and force ellipses on titles that fit.
              ctx.font = '600 ' + Math.round(titleFont) + 'px "Qanelas Soft", Poppins, sans-serif';
              ctx.fillStyle = '#000000'; /* black — matches the on-page tile (client 10/07/2026) */
              ctx.textBaseline = 'top';
              ctx.textAlign = 'center';
              wrapTitle(title, media).forEach(function (line, li) {
                ctx.fillText(line, x + frame / 2, rowY + titleTop + li * titleLineH + (titleLineH - titleFont) / 2);
              });
              ctx.textAlign = 'start';
            }
            ctx.restore();
          });

          return new Promise(function (resolve, reject) {
            canvas.toBlob(function (blob) {
              blob ? resolve(blob) : reject(new Error('canvas-export-failed'));
            }, 'image/png');
          });
        });
    }

    function handleShare() {
      var pageShare = {
        title: document.title,
        text: 'I got "' + personaName + '" — Meet The Montigo You!',
        url: shareUrl()
      };

      buildShareImage()
        .then(function (blob) {
          var file = new File([blob], 'montigo-personality.png', { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            return navigator.share({ files: [file], title: pageShare.title, text: pageShare.text });
          }
          // Web Share with files unsupported → download the card instead.
          var a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = 'montigo-personality.png';
          a.click();
          URL.revokeObjectURL(a.href);
        })
        .catch(function () {
          // No base card image yet (or export failed) → share/copy the link.
          if (navigator.share) {
            navigator.share(pageShare).catch(function () { /* user cancelled */ });
          } else if (navigator.clipboard) {
            navigator.clipboard.writeText(pageShare.url);
            alert('Link copied!');
          }
        });
    }

    root.querySelectorAll('[data-mty-share]').forEach(function (btn) {
      btn.addEventListener('click', handleShare);
    });

    /* ---------------------------------------------------------------- */
    /* "16 Personalities" — toggle the embedded all-16 grid section       */
    /* ---------------------------------------------------------------- */

    root.querySelectorAll('[data-mty-show-all]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var allRoot = document.querySelector('[data-mty-all]');
        if (!allRoot) {
          console.warn('[MTY] "Personality — All 16" section is missing from this page template.');
          return;
        }
        root.classList.add('mty-hidden');
        allRoot.classList.remove('mty-hidden');
        window.scrollTo(0, 0);
      });
    });

    /* ---------------------------------------------------------------- */
    /* Retake + Klaviyo placeholder                                       */
    /* ---------------------------------------------------------------- */

    root.querySelectorAll('[data-mty-retake]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        try { sessionStorage.removeItem(STORAGE_KEY); } catch (err) { /* noop */ }
        window.location.href = quizUrl;
      });
    });

    /* Placeholder handler — replaced by the Klaviyo embed once the client
       provides the form/list ID (paste it into the section's "Klaviyo embed
       code" setting; this placeholder form then never renders). */
    var klaviyoForm = root.querySelector('[data-mty-klaviyo-placeholder]');
    if (klaviyoForm) {
      klaviyoForm.addEventListener('submit', function (e) {
        e.preventDefault();
        console.info('[MTY] Klaviyo placeholder submit — waiting for real form ID.');
        klaviyoForm.querySelector('button').textContent = 'Thanks! (placeholder)';
      });
    }
  }

  init();
  stackAdminPreviews();

  /* Theme editor re-renders the section on setting changes — re-init. */
  document.addEventListener('shopify:section:load', function () {
    init();
    stackAdminPreviews();
  });
})();
